import type { TimeRange } from "../types"
import type { BucketUnit } from "./dataUtils"

const bucketTickOptions: Record<BucketUnit, Intl.DateTimeFormatOptions> = {
    hour: { hour: "numeric" },
    day: { month: "short", day: "numeric" },
    week: { month: "short", day: "numeric" },
    month: { month: "short", year: "numeric" },
}

export function makeBucketTickFormatter(unit: BucketUnit) {
    return (tickValue: Date | { valueOf(): number }) =>
        new Intl.DateTimeFormat("en-US", bucketTickOptions[unit]).format(
            new Date(tickValue.valueOf())
        )
}

const bucketTooltipOptions: Record<BucketUnit, Intl.DateTimeFormatOptions> = {
    hour: { hour: "numeric", month: "short", day: "numeric" },
    day: { month: "long", day: "numeric", year: "numeric" },
    week: { month: "long", day: "numeric", year: "numeric" },
    month: { month: "long", year: "numeric" },
}

export function makeBucketTooltipFormatter(unit: BucketUnit) {
    return new Intl.DateTimeFormat("en-US", bucketTooltipOptions[unit])
}

/**
 * Returns a tick formatter function for time-axis labels based on the active range.
 *
 * - "7d" / "1y" → short month name (e.g. "Jan")
 * - "30d"       → 1-based day index from startDate (e.g. "1", "2", … "30")
 * - "6m"        → full month + year (e.g. "January 2025")
 */
export function makeTimeRangeTickFormatter(range: TimeRange) {
    return (tickValue: Date | { valueOf(): number }): string => {
        const dateObj = new Date(tickValue.valueOf())

        if (range === "30d") {
            return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(
                dateObj
            )
        }

        if (range === "6m") {
            return new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric" }).format(
                dateObj
            )
        }

        if (range === "all") {
            return new Intl.DateTimeFormat("en-US", { month: "short", year: "numeric" }).format(
                dateObj
            )
        }

        return new Intl.DateTimeFormat("en-US", { month: "short" }).format(dateObj)
    }
}
